import { Component, Inject, OnInit, ChangeDetectionStrategy, signal } from '@angular/core'
import { Validators } from '@angular/forms'
import { FieldTree, form } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'

import { NaytettavaKirjaus } from '../kirjanpito.component'

import { CurrencyService } from '@lemon/core/service/currency.service'
import { DateService } from '@lemon/core/service/date.service'
import { arvonMuutokset } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from 'app/_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { Asiakas } from 'app/_jaettu-lemonator/model/asiakas'
import { Kirjanpitotili } from 'app/_jaettu-lemonator/model/kirjanpito'
import { AsiakasJaettuService } from 'app/_jaettu-lemonator/service/asiakas-jaettu.service'
import { PoistotService } from 'app/_jaettu-lemonator/service/poistot.service'
import { TilikarttaJaettuService } from 'app/_jaettu-lemonator/service/tilikartta-jaettu.service'
import { map, Observable, startWith } from 'rxjs'

export interface KirjausPoistotDialogData {
  naytettavaKirjaus: NaytettavaKirjaus
  automaattisenPoistonTili: Kirjanpitotili
  poistettavaMaara: number
  asiakas: Asiakas
}

export interface KirjausPoistotDialogResult {
  luoPoistokirjaukset: boolean
  suhteutaPoisto: boolean
  kokoPoistoViimeiselleKuukaudelle: boolean
}

interface PoistoLomake {
  luoPoistokirjaukset: boolean
  suhteutaPoisto: boolean
  kokoPoistoViimeiselleKuukaudelle: boolean
}

interface PoistonTiedot {
  tili: string
  lisays: number
  yhteensa: string
  kuukausia: number
  poistoPerKuukausi: string
}

@Component({
  templateUrl: './kirjaus.poistot.dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class KirjausPoistotDialog implements OnInit {
  lomake: FieldTree<PoistoLomake>
  poistonTiedotObservable: Observable<PoistonTiedot | null>

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: KirjausPoistotDialogData,
    private _dialogRef: MatDialogRef<KirjausPoistotDialog, KirjausPoistotDialogResult>,
    private _dateService: DateService,
    private _formValidationService: FormValidationService,
    private _asiakasJaettuService: AsiakasJaettuService,
    private _currencyService: CurrencyService,
    private _poistotService: PoistotService,
    private _tilikarttaJaettuService: TilikarttaJaettuService,
  ) {
    this.lomake = form(signal<PoistoLomake>({ luoPoistokirjaukset: true, suhteutaPoisto: false, kokoPoistoViimeiselleKuukaudelle: false }), polku => {
      validoiReaktiivisesti(polku.luoPoistokirjaukset, Validators.required)
      validoiReaktiivisesti(polku.suhteutaPoisto, Validators.required)
      validoiReaktiivisesti(polku.kokoPoistoViimeiselleKuukaudelle, Validators.required)
    })

    const kirjauksenPaivamaara = this.data.naytettavaKirjaus.uusiPaivamaara || this.data.naytettavaKirjaus.kirjaus.p

    const tilikausi = this._asiakasJaettuService.annaTilikausiPaivalleNumber(data.asiakas, kirjauksenPaivamaara)

    this.poistonTiedotObservable = arvonMuutokset(this.lomake).pipe(
      startWith(this.lomake().value()),
      map(value => {
        if (!value.luoPoistokirjaukset) {
          return null
        }

        const automaattisenPoistonTili = this.data.automaattisenPoistonTili
        // Luetaan raaka nimi (ei tarvita täällä kirjanpito.component.ts:n lisäämiä reskontra / poistomerkintöjä)
        const tilinNimi = this._tilikarttaJaettuService.annaKirjanpitotilinNimi(automaattisenPoistonTili, 'fi')

        const poistettavaMaara = this.data.poistettavaMaara
        const poistot = this._poistotService.laskePoistot(
          automaattisenPoistonTili.poistotiedot.poistoprosentti,
          this._dateService.localDateToNumber(tilikausi.loppuu),
          kirjauksenPaivamaara,
          poistettavaMaara,
          value.suhteutaPoisto,
          value.kokoPoistoViimeiselleKuukaudelle,
        )
        const poistoYhteensa = poistot.reduce((sum, d) => (sum += d.maara), 0)

        return {
          tili: `${automaattisenPoistonTili.numero} ${tilinNimi} (${automaattisenPoistonTili.poistotiedot.poistoprosentti}%)`,
          lisays: poistettavaMaara,
          yhteensa: this._currencyService.formatoiRahaIlmanValuuttaSymbolia(poistoYhteensa, 'fi'),
          kuukausia: poistot.length,
          poistoPerKuukausi: this._currencyService.formatoiRahaIlmanValuuttaSymbolia(poistot[0].maara, 'fi'),
        }
      }),
    )
  }

  ngOnInit() {}

  save() {
    if (this.lomake().invalid()) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    const { luoPoistokirjaukset, suhteutaPoisto, kokoPoistoViimeiselleKuukaudelle } = this.lomake().value()
    this._dialogRef.close({
      luoPoistokirjaukset: luoPoistokirjaukset,
      suhteutaPoisto: suhteutaPoisto,
      kokoPoistoViimeiselleKuukaudelle: kokoPoistoViimeiselleKuukaudelle,
    })
  }
}
